import { makeAutoObservable } from 'mobx';
import { DateTime } from 'luxon';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import {
  buildMarketDataCandleKey,
  buildMarketDataCandleKeys,
  buildMarketDataKeys,
} from './utils/marketDataRouting';
import {
  buildMinuteAnchorContext,
  floorTimeStampToBucket,
} from '../../components/displays/charts/d3/candlestickchart/newCandleChart/utils/normalizeChartCandles';

// PRD: docs/frontend-chart-timestamp-store-prd.md
//
// This store is the durable, timestamp-keyed chart history described in the PRD. It is
// intentionally separate from `PriceDataStore.candles`, which only ever keeps the single
// latest candle per route (no duration) for the live price bar. `ChartCandleStore` keeps a
// full history per route+timeframe, keyed by candle bucket timestamp, so late/out-of-order
// events land in the correct historical bucket instead of being dropped.

const TRADE_MESSAGE_TYPES = new Set(['trade', 'tradex', 'timesale']);
const QUOTE_MESSAGE_TYPES = new Set(['quote']);

function isFuturesFamilySymbol(symbol) {
  return typeof symbol === 'string' && (symbol.startsWith('/') || symbol.startsWith('./'));
}

function normalizeTimeStamp(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : null;
}

function normalizeCandleDuration(msg) {
  const explicitDuration = Number(msg?.candleDuration);
  if (Number.isFinite(explicitDuration) && explicitDuration > 0) {
    return explicitDuration;
  }
  const streamDuration = Number(msg?.duration);
  if (Number.isFinite(streamDuration) && streamDuration > 0) {
    return streamDuration;
  }
  return null;
}

/**
 * Merge rule for Functional Requirement #3 (backend `stream/candle` messages and `getChart`
 * seed candles). Both are treated as authoritative, complete-OHLCV sources from the backend,
 * as opposed to candles synthesized locally from trade/time-and-sale events (see
 * `mergeTradeDerivedCandle` below). A backend candle message always wins over whatever was in
 * the bucket before, including a trade-derived bucket — this is the fix for the open code-review
 * concern that backend data must not be permanently shadowed by a synthetic trade-derived volume
 * of `null`.
 */
function mergeBackendCandle(existing, incoming, source) {
  const incomingTimeStamp = normalizeTimeStamp(incoming.timeStamp);

  return {
    ...incoming,
    timeStamp: incomingTimeStamp,
    // Every backend candle is a complete snapshot of the bucket as the provider currently
    // knows it. Replace all OHLC fields so provider corrections can lower a bad high, raise a
    // bad low, or correct the open; envelope-merging made those errors permanent in the UI.
    open: Number(incoming.open),
    high: Number(incoming.high),
    low: Number(incoming.low),
    close: Number(incoming.close),
    // Backend-provided volume is authoritative for its own bucket; never sum repeated
    // messages for the same timestamp (PRD Functional Requirement #3).
    volume: Number(incoming.volume ?? 0),
    lastEventTimeStamp: incomingTimeStamp,
    source,
    isSyntheticFromTrades: false,
  };
}

/**
 * Aggregates a live 1-minute backend candle into a coarser display bucket (e.g. a 5-minute
 * chart) keyed by `bucketTimeStamp`. See docs/tuui-live-candle-advance-spec.md: the backend
 * only pushes 1-minute `stream/candle` messages, so a chart on a larger duration must fold each
 * minute onto its own bucket grid or it never advances to the next bucket between getChart
 * re-fetches.
 *
 * Constituent minutes are tracked per source-minute timestamp in `minuteContributions` so that
 * repeated "forming" updates for the same minute (`completeCandle: false`, sent multiple times
 * as DxFeed refines the minute) replace that minute's value rather than double-counting its
 * volume, while distinct minutes within the bucket are summed. open = earliest minute's open,
 * close = latest minute's close, high/low = extents, volume = sum across minutes. The result is
 * marked backend-sourced (`source: 'candle'`) so it overrides any prior trade/quote-derived
 * bucket for the same grid slot rather than being shadowed by synthetic data.
 */
function aggregateLiveCandleIntoBucket(existing, incoming, sourceTimeStamp, bucketTimeStamp) {
  const contributions = { ...(existing?.minuteContributions ?? {}) };
  contributions[sourceTimeStamp] = {
    open: Number(incoming.open),
    high: Number(incoming.high),
    low: Number(incoming.low),
    close: Number(incoming.close),
    volume: Number(incoming.volume ?? 0),
    completeCandle: Boolean(incoming.completeCandle),
  };

  const sourceTimeStamps = Object.keys(contributions)
    .map(Number)
    .sort((left, right) => left - right);
  const firstContribution = contributions[sourceTimeStamps[0]];
  const lastContribution = contributions[sourceTimeStamps[sourceTimeStamps.length - 1]];
  const baseVolume = existing && !existing.minuteContributions
    ? Number(existing.volume ?? 0)
    : Number(existing?.baseVolume ?? 0);

  let high = existing ? Number(existing.high) : -Infinity;
  let low = existing ? Number(existing.low) : Infinity;
  let contributionVolume = 0;
  let allComplete = true;
  sourceTimeStamps.forEach((ts) => {
    const contribution = contributions[ts];
    high = Math.max(high, contribution.high);
    low = Math.min(low, contribution.low);
    contributionVolume += contribution.volume;
    allComplete = allComplete && contribution.completeCandle;
  });

  const open = existing ? Number(existing.open) : firstContribution.open;
  const close = lastContribution.close;
  const volume = baseVolume + contributionVolume;

  return {
    ...(existing ?? {}),
    symbol: incoming.symbol ?? existing?.symbol ?? null,
    timeStamp: bucketTimeStamp,
    open,
    high,
    low,
    close,
    volume,
    baseVolume,
    completeCandle: allComplete,
    red: close < open,
    green: close > open,
    minuteContributions: contributions,
    lastEventTimeStamp: sourceTimeStamps[sourceTimeStamps.length - 1],
    source: 'candle',
    isSyntheticFromTrades: false,
    isSyntheticFromQuotes: false,
  };
}

/**
 * Merge rule for Functional Requirement #4 (trade/time-and-sale derived candles). Volume is
 * pinned to `null` in v1 per the revised PRD: the trade/timesale stream has no stable trade
 * identity, so reconnects/replays could double-count a summed volume. If `existing` is already
 * backend-sourced (a real `stream/candle` message or a `getChart` seed candle for this bucket),
 * the trade event is allowed to refine high/low/close as a "freshest tick" signal, but it must
 * not downgrade the bucket to synthetic or null out the backend-provided volume.
 */
function mergeTradeDerivedCandle(existing, { timeStamp, price, eventTimeStamp }) {
  if (!existing) {
    return {
      timeStamp,
      open: price,
      high: price,
      low: price,
      close: price,
      volume: null,
      lastEventTimeStamp: eventTimeStamp,
      source: 'trade',
      isSyntheticFromTrades: true,
    };
  }

  const isNewerEvent = eventTimeStamp >= (existing.lastEventTimeStamp ?? -Infinity);
  const high = Math.max(existing.high, price);
  const low = Math.min(existing.low, price);
  const close = isNewerEvent ? price : existing.close;
  const lastEventTimeStamp = Math.max(
    existing.lastEventTimeStamp ?? -Infinity,
    eventTimeStamp
  );

  const isBackendSourced = existing.source !== 'trade' && existing.source !== 'quote';
  if (isBackendSourced) {
    return { ...existing, high, low, close, lastEventTimeStamp };
  }

  return {
    ...existing,
    high,
    low,
    close,
    lastEventTimeStamp,
    volume: null,
    source: 'trade',
    isSyntheticFromTrades: true,
    isSyntheticFromQuotes: existing.isSyntheticFromQuotes,
  };
}

function mergeQuoteDerivedCandle(existing, { timeStamp, price, eventTimeStamp }) {
  const next = mergeTradeDerivedCandle(existing, {
    timeStamp,
    price,
    eventTimeStamp,
  });

  if (!existing) {
    return {
      ...next,
      source: 'quote',
      isSyntheticFromQuotes: true,
    };
  }

  if (existing.source !== 'trade' && existing.source !== 'quote') {
    return next;
  }

  return {
    ...next,
    source: existing.source === 'trade' ? 'trade' : 'quote',
    isSyntheticFromTrades: existing.isSyntheticFromTrades,
    isSyntheticFromQuotes:
      existing.isSyntheticFromQuotes || existing.source === 'quote',
  };
}

function getQuotePriceAndTimeStamp(msg) {
  const bid = Number(msg?.bid);
  const ask = Number(msg?.ask);
  const hasBid = Number.isFinite(bid) && bid > 0;
  const hasAsk = Number.isFinite(ask) && ask > 0;
  const price = hasBid && hasAsk ? (bid + ask) / 2 : hasBid ? bid : hasAsk ? ask : null;

  // Use receivedAt (server wall-clock at ingest) for candle bucket placement.
  // Exchange biddate/askdate reflect when the bid/ask last changed — potentially minutes
  // old for stable markets — which would bucket the quote into a historical minute and
  // create a flat OHLC line. receivedAt is always current and monotonically increasing.
  const eventTimeStamp = normalizeTimeStamp(
    msg?.receivedAt
      ?? msg?.time
      ?? msg?.date
      ?? msg?.biddate
      ?? msg?.askdate
  );

  if (!Number.isFinite(price) || !Number.isFinite(eventTimeStamp)) {
    return null;
  }

  return { price, eventTimeStamp };
}

export class ChartCandleStore {
  root;

  // histories[routeKey] = { candlesByTimeStamp: { [timeStamp]: candle }, sessionWindows: [], lastUpdatedAt }
  histories = {};

  // activeRoutes[routeKey] = { symbol, dataSource, brokerage, timeUnit, candleDuration, refCount }
  //
  // A bare trade/timesale message carries no timeUnit/candleDuration — bucketing depends on
  // which chart(s) are currently viewing this symbol+route. `registerRoute`/`unregisterRoute`
  // (called by RefactoredChartContainer on mount/param-change/unmount) tell the store which
  // timeframes are actually in view, so a single trade tick can be bucketed into every
  // currently-rendered duration for that symbol (e.g. a 1-minute and a 5-minute chart open on
  // the same Tradier symbol at once).
  activeRoutes = {};

  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
    this.listenForMessages();
  }

  listenForMessages = () => {
    groupByFunction('messageType', 'stream', this.root.websocketSubject).subscribe(
      (msg) => {
        if (msg.type === 'candle') {
          this.handleCandleMessage(msg);
        } else if (TRADE_MESSAGE_TYPES.has(msg.type)) {
          this.handleTradeMessage(msg);
        } else if (QUOTE_MESSAGE_TYPES.has(msg.type)) {
          this.handleQuoteMessage(msg);
        }
      }
    );

    groupByFunction(
      'messageType',
      'removeSymbol',
      this.root.websocketSubject
    ).subscribe((msg) => {
      this.handleRemoveSymbol(msg);
    });
  };

  ensureHistory = (routeKey) => {
    if (!this.histories[routeKey]) {
      this.histories[routeKey] = {
        candlesByTimeStamp: {},
        sessionWindows: [],
        lastUpdatedAt: null,
      };
    }
    return this.histories[routeKey];
  };

  registerRoute = ({ symbol, dataSource, brokerage, timeUnit, candleDuration }) => {
    const routeKey = buildMarketDataCandleKey({
      symbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
    });
    if (!routeKey) {
      return null;
    }

    this.ensureHistory(routeKey);
    const existing = this.activeRoutes[routeKey];
    if (existing) {
      existing.refCount += 1;
    } else {
      this.activeRoutes[routeKey] = {
        symbol,
        dataSource,
        brokerage,
        timeUnit,
        candleDuration,
        refCount: 1,
      };
    }
    return routeKey;
  };

  unregisterRoute = (routeKey) => {
    const existing = this.activeRoutes[routeKey];
    if (!existing) {
      return;
    }
    existing.refCount -= 1;
    if (existing.refCount <= 0) {
      delete this.activeRoutes[routeKey];
    }
    // Note: history is intentionally kept around after the last unregister. A chart that
    // remounts (tab switch, parameter round-trip) should see its candles immediately rather
    // than re-requesting and waiting on the network (PRD Goal #7: never render a blank chart
    // when data is already known).
  };

  seedFromGetChart = ({
    symbol,
    dataSource,
    brokerage,
    timeUnit,
    candleDuration,
    candles,
    sessionWindows,
  }) => {
    const routeKey = buildMarketDataCandleKey({
      symbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
    });
    if (!routeKey) {
      return null;
    }
    // Seeding only ensures the history bucket exists — it does not affect the
    // registerRoute/unregisterRoute refcount. The chart container separately registers the
    // route for the lifetime of the mounted component so trade ticks know which timeframes
    // to bucket into.
    const history = this.ensureHistory(routeKey);
    if (Array.isArray(sessionWindows)) {
      history.sessionWindows = sessionWindows;
    }

    (candles ?? []).forEach((candle) => {
      const timeStamp = normalizeTimeStamp(candle?.timeStamp);
      if (timeStamp === null) {
        return;
      }
      const existing = history.candlesByTimeStamp[timeStamp];
      history.candlesByTimeStamp[timeStamp] = mergeBackendCandle(
        existing,
        { ...candle, timeStamp },
        'chart'
      );
    });
    history.lastUpdatedAt = Date.now();
    return routeKey;
  };

  handleCandleMessage = (msg) => {
    const candleDuration = normalizeCandleDuration(msg);
    const timeUnit = msg.timeUnit;
    const timeStamp = normalizeTimeStamp(msg.timeStamp);
    if (!candleDuration || !timeUnit || timeStamp === null) {
      // Can't bucket a candle message without knowing its timeframe — same constraint
      // PriceDataStore.handleCandle already lives with for its single-latest-candle cache.
      return;
    }

    const ownRouteKeys = buildMarketDataCandleKeys({
      symbol: msg.symbol,
      dataSource: msg.dataSource || msg.datasource,
      brokerage: msg.brokerage || msg.from,
      timeUnit,
      candleDuration,
    }).filter(Boolean);

    // Store into the route(s) whose timeframe matches the message's own duration. This is the
    // authoritative same-grid path (e.g. a 1-minute chart receiving 1-minute candles) and runs
    // whether or not a chart is currently registered for the route.
    ownRouteKeys.forEach((routeKey) => {
      const history = this.ensureHistory(routeKey);
      const existing = history.candlesByTimeStamp[timeStamp];
      history.candlesByTimeStamp[timeStamp] = mergeBackendCandle(
        existing,
        { ...msg, timeStamp },
        'candle'
      );
      history.lastUpdatedAt = Date.now();
    });

    // Advance coarser display routes (e.g. a 5-minute /ES chart) from the live 1-minute candle
    // stream. The backend only pushes 1-minute `stream/candle` messages, so a chart on a larger
    // duration must aggregate those minutes onto its own bucket grid or it never opens the next
    // bucket between getChart re-fetches (docs/tuui-live-candle-advance-spec.md). Only currently
    // viewed routes are known here (via registerRoute), mirroring the trade/quote paths. Routes
    // already handled above (same timeUnit+duration as the message) are skipped so we never
    // double-process the matching-grid route.
    const ownRouteKeySet = new Set(ownRouteKeys);
    this.routesMatchingMessage(msg).forEach(([routeKey, route]) => {
      if (ownRouteKeySet.has(routeKey)) {
        return;
      }
      const history = this.ensureHistory(routeKey);
      const bucketTimeStamp = this.resolveTradeBucketTimeStamp({
        symbol: route.symbol,
        eventTimeStamp: timeStamp,
        timeUnit: route.timeUnit,
        candleDuration: route.candleDuration,
        sessionWindows: history.sessionWindows,
        existingCandles: Object.values(history.candlesByTimeStamp),
      });
      if (bucketTimeStamp === null) {
        return;
      }
      const existing = history.candlesByTimeStamp[bucketTimeStamp];
      history.candlesByTimeStamp[bucketTimeStamp] = aggregateLiveCandleIntoBucket(
        existing,
        { ...msg, timeStamp },
        timeStamp,
        bucketTimeStamp
      );
      history.lastUpdatedAt = Date.now();
    });
  };

  routesMatchingMessage = (msg) => {
    const messageRouteKeys = new Set(
      [
        ...buildMarketDataKeys({
          symbol: msg.symbol,
          dataSource: msg.dataSource || msg.datasource,
          brokerage: msg.brokerage || msg.from,
        }),
        ...(msg.from ? buildMarketDataKeys({ symbol: msg.symbol, from: msg.from }) : []),
      ].filter(Boolean)
    );

    return Object.entries(this.activeRoutes).filter(([, route]) => {
      const routeBaseKeys = buildMarketDataKeys({
        symbol: route.symbol,
        dataSource: route.dataSource,
        brokerage: route.brokerage,
      });
      return routeBaseKeys.some((baseKey) => messageRouteKeys.has(baseKey));
    });
  };

  handleTradeMessage = (msg) => {
    const price = Number(msg.price ?? msg.last);
    const eventTimeStamp = normalizeTimeStamp(msg.date);
    if (!Number.isFinite(price) || eventTimeStamp === null) {
      return;
    }

    this.routesMatchingMessage(msg).forEach(([routeKey, route]) => {
      const history = this.ensureHistory(routeKey);
      const bucketTimeStamp = this.resolveTradeBucketTimeStamp({
        symbol: route.symbol,
        eventTimeStamp,
        timeUnit: route.timeUnit,
        candleDuration: route.candleDuration,
        sessionWindows: history.sessionWindows,
        existingCandles: Object.values(history.candlesByTimeStamp),
      });
      if (bucketTimeStamp === null) {
        return;
      }

      const existing = history.candlesByTimeStamp[bucketTimeStamp];
      history.candlesByTimeStamp[bucketTimeStamp] = mergeTradeDerivedCandle(existing, {
        timeStamp: bucketTimeStamp,
        price,
        eventTimeStamp,
      });
      history.lastUpdatedAt = Date.now();
    });
  };

  handleQuoteMessage = (msg) => {
    const quote = getQuotePriceAndTimeStamp(msg);
    if (!quote) {
      return;
    }

    this.routesMatchingMessage(msg).forEach(([routeKey, route]) => {
      const history = this.ensureHistory(routeKey);
      const bucketTimeStamp = this.resolveTradeBucketTimeStamp({
        symbol: route.symbol,
        eventTimeStamp: quote.eventTimeStamp,
        timeUnit: route.timeUnit,
        candleDuration: route.candleDuration,
        sessionWindows: history.sessionWindows,
        existingCandles: Object.values(history.candlesByTimeStamp),
      });
      if (bucketTimeStamp === null) {
        return;
      }

      const existing = history.candlesByTimeStamp[bucketTimeStamp];
      history.candlesByTimeStamp[bucketTimeStamp] = mergeQuoteDerivedCandle(existing, {
        timeStamp: bucketTimeStamp,
        price: quote.price,
        eventTimeStamp: quote.eventTimeStamp,
      });
      history.lastUpdatedAt = Date.now();
    });
  };

  // Reuses normalizeChartCandles' bucketing math (session-aware for futures, 9:30 anchor for
  // equities) instead of re-deriving candle-start-time rules here, per the PRD's "use the
  // existing normalizeChartCandles pipeline" requirement. We call the lower-level
  // floorTimeStampToBucket/buildMinuteAnchorContext exports directly rather than going through
  // normalizeChartCandles() itself: that function intentionally treats any candleDuration <= 1
  // as "already bucketed by the provider" and returns the raw timestamp unchanged (correct for
  // getChart/stream candle payloads). A raw trade/timesale tick is never pre-bucketed, including
  // for 1-minute charts, so that shortcut would silently disable bucketing for the most common
  // chart timeframe.
  resolveTradeBucketTimeStamp = ({
    symbol,
    eventTimeStamp,
    timeUnit,
    candleDuration,
    sessionWindows,
    existingCandles = [],
  }) => {
    let minuteAnchorContext = null;
    if (String(timeUnit).toUpperCase() === 'MINUTES') {
      minuteAnchorContext = buildMinuteAnchorContext({
        candles: [{ symbol, timeStamp: eventTimeStamp }],
        symbol,
        sessionWindows,
        referenceTimeStamp: eventTimeStamp,
      });

      if (!minuteAnchorContext) {
        minuteAnchorContext = this.buildFallbackMinuteAnchorContext({
          symbol,
          eventTimeStamp,
          existingCandles,
        });
      }
    }

    const bucketed = floorTimeStampToBucket(
      eventTimeStamp,
      timeUnit,
      candleDuration,
      minuteAnchorContext
    );
    return normalizeTimeStamp(bucketed);
  };

  buildFallbackMinuteAnchorContext = ({ symbol, eventTimeStamp, existingCandles }) => {
    if (!isFuturesFamilySymbol(symbol)) {
      return null;
    }

    const anchors = (existingCandles ?? [])
      .map((candle) => normalizeTimeStamp(candle?.timeStamp))
      .filter((timeStamp) => timeStamp !== null)
      .sort((left, right) => left - right);
    if (anchors.length === 0) {
      return null;
    }

    const priorAnchor = [...anchors]
      .reverse()
      .find((timeStamp) => timeStamp <= eventTimeStamp);
    const anchorTimeStamp = priorAnchor ?? anchors[0];
    return {
      anchor: DateTime.fromMillis(anchorTimeStamp, { zone: 'America/New_York' }),
    };
  };

  handleRemoveSymbol = (msg) => {
    const baseKeys = buildMarketDataKeys({
      symbol: msg.symbol,
      dataSource: msg.datasource,
      brokerage: msg.brokerage,
    });
    if (baseKeys.length === 0) {
      return;
    }
    const prefixes = baseKeys.map((baseKey) => `${baseKey}|`);
    Object.keys(this.histories).forEach((routeKey) => {
      if (prefixes.some((prefix) => routeKey.startsWith(prefix))) {
        // A removeSymbol message can race with a chart mount/addSymbol response. Keep
        // a route that is actively being viewed; the chart lifecycle will unregister
        // it when the view really goes away. Evict inactive routes so stale history
        // cannot survive an authoritative backend removal.
        const route = this.activeRoutes[routeKey];
        if (!route || route.refCount <= 0) {
          delete this.histories[routeKey];
          delete this.activeRoutes[routeKey];
        }
      }
    });
  };

  getHistory = ({ symbol, dataSource, brokerage, timeUnit, candleDuration, limit }) => {
    const routeKey = buildMarketDataCandleKey({
      symbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
    });
    const history = this.histories[routeKey];
    if (!history) {
      return [];
    }

    const sorted = Object.values(history.candlesByTimeStamp).sort(
      (left, right) => left.timeStamp - right.timeStamp
    );
    return Number.isFinite(limit) ? sorted.slice(-limit) : sorted;
  };

  getSessionWindows = ({ symbol, dataSource, brokerage, timeUnit, candleDuration }) => {
    const routeKey = buildMarketDataCandleKey({
      symbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
    });
    return this.histories[routeKey]?.sessionWindows ?? [];
  };

  hasCandle = ({ symbol, dataSource, brokerage, timeUnit, candleDuration, timeStamp }) => {
    const routeKey = buildMarketDataCandleKey({
      symbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
    });
    const normalizedTimeStamp = normalizeTimeStamp(timeStamp);
    if (normalizedTimeStamp === null) {
      return false;
    }
    return Boolean(this.histories[routeKey]?.candlesByTimeStamp?.[normalizedTimeStamp]);
  };

  getCandle = ({ symbol, dataSource, brokerage, timeUnit, candleDuration, timeStamp }) => {
    const routeKey = buildMarketDataCandleKey({
      symbol,
      dataSource,
      brokerage,
      timeUnit,
      candleDuration,
    });
    const normalizedTimeStamp = normalizeTimeStamp(timeStamp);
    if (normalizedTimeStamp === null) {
      return null;
    }
    return this.histories[routeKey]?.candlesByTimeStamp?.[normalizedTimeStamp] ?? null;
  };
}
